import { Injectable } from "@angular/core";
import { Share } from "../model/Share";
import { CreateShareDto } from "../dto/create-share.dto";
import { ReadShareQuery } from "../model/ReadShareQuery";
import { UpdateShareDto } from "../dto/update-share.dto";
import { Store } from "@ngrx/store";
import { firstValueFrom } from "rxjs";
import { AccountType, ImageUtil } from "@qid/core";
import { CreateShareForUnregisteredDto } from "../dto/create-share-for-unregistered.dto";
import { RestService } from "../../../services/rest.service";
import { RestResponse } from "../../../interface/RestInterface";
import { AuthSelectors } from "../../authentication";
import { Participant } from "../model/participant";
import { UpdateParticipantDto } from "../dto/update-participant.dto";
import { AddParticipantDto } from "../dto/add-participant.dto";

@Injectable({
  providedIn: 'root'
})
export class ShareService {
  static readonly ApiEndpoint = "share/";

  constructor(
    private restService: RestService,
    private store: Store
  ) {
  }

  public async createShare(
    createShareDto: CreateShareDto
  ): Promise<Share> {

    return (
      await this.restService.send<Share>({
        url: ShareService.ApiEndpoint,
        body: createShareDto,
        method: "POST"
      })
    ).data;
  }

  public async createShareForUnregistered(
    createShareForUnregisteredDto: CreateShareForUnregisteredDto
  ): Promise<RestResponse<Share>> {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'create-for-unregistered',
      body: createShareForUnregisteredDto,
      method: "POST"
    })
  }

  async read(id: string): Promise<RestResponse<Share>> {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + id,
      method: "GET"
    })
  }

  async readAll(query: ReadShareQuery, account_id: string, accountType: AccountType) {

    return (
      await this.restService.send<any>({
        url: 'account-share',
        method: 'GET',
        query: query,
        account_id: account_id,
        accountType: accountType
      })
    ).data;
  }

  async update(id: string, updateDto: UpdateShareDto) {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + id,
      method: "PUT",
      body: updateDto
    })
  }

  async updateAsOrganiser(id: string, updateDto: UpdateShareDto) {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'organiser/' + id,
      method: "PUT",
      body: updateDto
    })
  }

  public async delete(shareId: string): Promise<any> {
    const account_id = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId))
    return this.restService.send({
      url: ShareService.ApiEndpoint + shareId,
      method: 'DELETE',
      account_id: account_id,
      accountType: AccountType.organisation
    });
  }

  async unlock(_id): Promise<Share> {
    return (await this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'unlock/' + _id,
      method: "PUT"
    })).data
  }

  async uploadFacePhoto(imageFile, share_id: string) {
    const data = await ImageUtil.getCompressedFormData([imageFile])
    return this.restService.sendFiles<{ image: string }>({
      url: 'share/face-photo',
      body: data,
      method: 'POST',
      isFile: true,
      query: {
        share_id: share_id
      }
    });
  }

  fetchAnalytics(templateIds: string[], startDate: any, endDate: any, granularity, showBranchAccounts: boolean) {
    return this.restService.send({
      url: 'share/analytics',
      method: 'GET',
      query: {
        templateIds: templateIds,
        startDate: startDate,
        endDate: endDate,
        granularity: granularity,
        showBranchAccounts: showBranchAccounts
      }
    })
  }

  joinParticipant(shareId: string): Promise<RestResponse<Share>> {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'join-participant/' + shareId,
      method: 'PUT',
    })
  }

  updateParticipant(shareId:string,participantId:string, updateParticipantDto:UpdateParticipantDto): Promise<RestResponse<Participant>> {
    return this.restService.send<Participant>({
      url: ShareService.ApiEndpoint + 'update-participant',
      method: "PUT",
      body : updateParticipantDto,
      query : {
        shareId: shareId,
        participantAccountId: participantId
      }
    })
  }

  addParticipant(shareId: string, addParticipantDto: AddParticipantDto = {}): Promise<RestResponse<Share>> {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'add-participants',
      method: 'POST',
      body: addParticipantDto,
      query: {
        shareId,
      },
    });
  }

  removeParticipant(shareId: string, participantIndex: number): Promise<RestResponse<Share>> {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'remove-participant',
      method: 'POST',
      query: {
        shareId,
        participantIndex,
      },
    });
  }

  /** Transfers admin role to the given participant. */
  makeParticipantAdmin(shareId: string, participantAccountId: string): Promise<RestResponse<Share>> {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'make-participant-admin',
      method: 'PUT',
      query: {
        shareId,
        participantAccountId,
      },
    });
  }

  /** Removes admin role from the given participant. */
  dismissParticipantAdmin(shareId: string, participantAccountId: string): Promise<RestResponse<Share>> {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'dismiss-participant-admin',
      method: 'PUT',
      query: {
        shareId,
        participantAccountId,
      },
    });
  }

  /** Marks participant completed and may complete the share when all participants are done. */
  completeParticipant(shareId: string, accountId: string): Promise<RestResponse<Share>> {
    return this.restService.send<Share>({
      url: ShareService.ApiEndpoint + 'complete-participant',
      method: 'PUT',
      query: {
        shareId,
        accountId,
      },
    });
  }

}
